import React, { useState, useEffect } from 'react';
import {
    ShoppingCart,
    Clock,
    ChefHat,
    CheckCircle,
    DollarSign,
    Sparkles,
    RefreshCw,
    TrendingUp,
    ArrowUp,
    AlertCircle
} from 'lucide-react';
import statisticsService from '../services/statistics';
import { useLanguage } from '../contexts/LanguageContext';

const SimpleStatistics = () => {
    const { t } = useLanguage();
    const [stats, setStats] = useState({
        orders_today: 0,
        pending_orders: 0,
        awaiting_preparation: 0,
        completed: 0,
        revenue: '0.00',
        suggestions_accepted_today: 0,
        suggestions_accepted_total: 0
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        setIsVisible(true);
    }, []);

    const fetchStats = async (showRefreshState = false) => {
        try {
            if (showRefreshState) {
                setIsRefreshing(true);
            }
            setError(null);
            const response = await statisticsService.getTodayStats();

            if (response && typeof response === 'object') {
                setStats(response);
            } else {
                console.error('Unexpected response format:', response);
                setError('Invalid response format from server');
            }
        } catch (error) {
            console.error('Error fetching stats:', error);
            setError(error.message || 'Failed to fetch statistics');
        } finally {
            setLoading(false);
            setIsRefreshing(false);
        }
    };

    useEffect(() => {
        fetchStats();

        // Refresh every 30 seconds
        const interval = setInterval(() => fetchStats(true), 30000);
        return () => clearInterval(interval);
    }, []);

    const handleManualRefresh = () => {
        fetchStats(true);
    };

    // Stats configuration with icons and gradients
    const statCards = [
        {
            icon: ShoppingCart,
            title: t('statistics.ordersToday', 'Orders Today'),
            value: stats.orders_today,
            gradient: 'from-blue-500 to-cyan-500',
            bgGradient: 'from-blue-50 to-cyan-50',
            borderColor: 'border-blue-100'
        },
        {
            icon: Clock,
            title: t('statistics.pendingOrders', 'Pending Orders'),
            value: stats.pending_orders,
            gradient: 'from-yellow-500 to-amber-500',
            bgGradient: 'from-yellow-50 to-amber-50',
            borderColor: 'border-yellow-100'
        },
        {
            icon: ChefHat,
            title: t('statistics.awaitingPreparation', 'Awaiting Preparation'),
            value: stats.awaiting_preparation,
            gradient: 'from-orange-500 to-red-500',
            bgGradient: 'from-orange-50 to-red-50',
            borderColor: 'border-orange-100'
        },
        {
            icon: CheckCircle,
            title: t('statistics.completedToday', 'Completed Today'),
            value: stats.completed,
            gradient: 'from-green-500 to-emerald-500',
            bgGradient: 'from-green-50 to-emerald-50',
            borderColor: 'border-green-100'
        },
        {
            icon: DollarSign,
            title: t('statistics.revenueToday', 'Revenue Today'),
            value: `CHF ${stats.revenue}`,
            gradient: 'from-purple-500 to-violet-500',
            bgGradient: 'from-purple-50 to-violet-50',
            borderColor: 'border-purple-100'
        },
        {
            icon: Sparkles,
            title: t('statistics.suggestionsAccepted', 'Suggestions Accepted'),
            value: stats.suggestions_accepted_today,
            gradient: 'from-pink-500 to-rose-500',
            bgGradient: 'from-pink-50 to-rose-50',
            borderColor: 'border-pink-100',
            subtitle: t('statistics.totalAllTime', 'Cumulative total:') + ` ${stats.suggestions_accepted_total}`
        }
    ];

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30 flex items-center justify-center">
                <div className="text-center">
                    <div className="relative">
                        <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-8 h-8 bg-blue-600 rounded-full animate-pulse"></div>
                        </div>
                    </div>
                    <p className="text-gray-600 font-medium">{t('statistics.loading', 'Loading statistics...')}</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-xl border border-red-100 p-8 max-w-md w-full text-center">
                    <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <AlertCircle className="h-10 w-10 text-red-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{t('statistics.failedToLoad', 'Failed to Load Statistics')}</h3>
                    <p className="text-gray-600 mb-6">{error}</p>
                    <button
                        onClick={handleManualRefresh}
                        className="inline-flex items-center gap-2 bg-red-600 text-white px-6 py-3 rounded-xl font-medium hover:bg-red-700 transition-all duration-300 hover:shadow-lg"
                    >
                        <RefreshCw className="h-4 w-4" />
                        {t('statistics.tryAgain', 'Try Again')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30 p-4 md:p-6 lg:p-8">
            {/* Animated Background */}
            <div className="fixed inset-0 -z-10 h-full w-full bg-white bg-[linear-gradient(to_right,#f0f0f0_1px,transparent_1px),linear-gradient(to_bottom,#f0f0f0_1px,transparent_1px)] bg-[size:6rem_4rem]">
                <div className="absolute bottom-0 left-0 right-0 top-0 bg-[radial-gradient(circle_800px_at_50%_100px,#f0f9ff,transparent)]"></div>
            </div>

            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className={`mb-8 transition-all duration-700 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div>
                            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 px-3 py-1 rounded-full text-sm font-medium mb-3">
                                <Sparkles className="h-4 w-4" />
                                {t('statistics.liveStatistics', 'Live Statistics')}
                            </div>
                            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900">
                                {t('statistics.todaysOverview', 'Today\'s Overview')}
                            </h1>
                            <p className="text-gray-600 mt-2">
                                {t('statistics.realTimeMetrics', 'Real-time statistics and performance metrics for today')}
                            </p>
                        </div>

                        {/* Refresh Button */}
                        <button
                            onClick={handleManualRefresh}
                            disabled={isRefreshing}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl text-gray-700 font-medium hover:bg-gray-50 hover:border-gray-300 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow group"
                        >
                            <RefreshCw className={`h-4 w-4 transition-transform duration-500 ${isRefreshing ? 'animate-spin' : 'group-hover:rotate-180'}`} />
                            <span>{isRefreshing ? t('statistics.refreshing', 'Refreshing...') : t('statistics.refresh', 'Refresh')}</span>
                        </button>
                    </div>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                    {statCards.map((stat, index) => {
                        const Icon = stat.icon;
                        return (
                            <div
                                key={index}
                                className={`group bg-white rounded-xl md:rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-500 hover:-translate-y-1 border border-gray-100 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}
                                style={{ transitionDelay: `${index * 100}ms` }}
                            >
                                <div className="flex items-start justify-between mb-4">
                                    <div className={`p-3 rounded-xl bg-gradient-to-r ${stat.gradient} text-white group-hover:scale-110 transition-transform duration-300`}>
                                        <Icon className="h-6 w-6" />
                                    </div>

                                    {/* Trend indicator (example - you can make this dynamic) */}
                                    {stat.value > 0 && (
                                        <div className="flex items-center gap-1 text-sm font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full">
                                            <ArrowUp className="h-3 w-3" />
                                            <span>+{Math.floor(Math.random() * 20)}%</span>
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <p className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">
                                        {stat.value}
                                    </p>
                                    <p className="text-sm font-medium text-gray-600 mb-1">
                                        {stat.title}
                                    </p>
                                    {stat.subtitle && (
                                        <p className="text-xs text-gray-500 mt-2 border-t border-gray-100 pt-2">
                                            {stat.subtitle}
                                        </p>
                                    )}
                                </div>

                                {/* Mini sparkline/decoration */}
                                <div className="absolute bottom-0 right-0 opacity-5 group-hover:opacity-10 transition-opacity duration-300">
                                    <Icon className="h-20 w-20 text-gray-900" />
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Additional Info Section - Suggestions Performance */}
                <div className={`mt-8 bg-white rounded-2xl shadow-lg border border-gray-100 p-6 transition-all duration-700 delay-700 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 bg-gradient-to-r from-pink-500 to-rose-500 rounded-lg">
                            <TrendingUp className="h-5 w-5 text-white" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">{t('statistics.suggestionPerformance', 'Suggestion Performance')}</h2>
                            <p className="text-sm text-gray-600">{t('statistics.suggestionPerformanceDesc', 'How suggestions are performing today')}</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-gradient-to-br from-pink-50 to-rose-50 p-4 rounded-xl border border-pink-100">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-gray-600">{t('statistics.acceptedToday', 'Accepted Today')}</span>
                                <Sparkles className="h-4 w-4 text-pink-500" />
                            </div>
                            <p className="text-2xl font-bold text-gray-900">{stats.suggestions_accepted_today}</p>
                        </div>

                        <div className="bg-gradient-to-br from-purple-50 to-violet-50 p-4 rounded-xl border border-purple-100">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-gray-600">{t('statistics.totalAllTimeHeader', 'Cumulative total')}</span>
                                <TrendingUp className="h-4 w-4 text-purple-500" />
                            </div>
                            <p className="text-2xl font-bold text-gray-900">{stats.suggestions_accepted_total}</p>
                        </div>
                    </div>

                    {/* Conversion Rate Indicator */}
                    {stats.suggestions_accepted_total > 0 && (
                        <div className="mt-4 p-4 bg-gray-50 rounded-xl">
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-gray-600">{t('statistics.conversionRate', 'Conversion Rate')}</span>
                                <span className="text-lg font-bold text-gray-900">
                                    {((stats.suggestions_accepted_today / (stats.suggestions_accepted_total || 1)) * 100).toFixed(1)}%
                                </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                                <div
                                    className="bg-gradient-to-r from-pink-500 to-rose-500 h-2 rounded-full transition-all duration-500"
                                    style={{ width: `${(stats.suggestions_accepted_today / (stats.suggestions_accepted_total || 1)) * 100}%` }}
                                ></div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Last updated timestamp */}
                <div className="mt-6 text-center text-sm text-gray-500">
                    {t('statistics.lastUpdated', 'Last updated:')} {new Date().toLocaleTimeString()}
                </div>
            </div>
        </div>
    );
};

export default SimpleStatistics;